import {
  collection,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Notification Service
 * Handles creating, fetching, and managing family member notifications
 */

/**
 * Notification types
 */
export const NOTIFICATION_TYPES = {
  ANNOUNCEMENT: 'announcement',
  EVENT: 'event',
  EMERGENCY_INFO: 'emergency_info',
  HEALTH_EMERGENCY: 'health_emergency',
  HEALTH_UPDATE: 'health_update',
  DISCUSSION: 'discussion',
  RECIPE: 'recipe',
  RECOMMENDATION: 'recommendation',
  GOAL: 'goal',
  INVITATION: 'invitation',
  MEMBER_JOINED: 'member_joined',
  TAG: 'tag',
  MESSAGE: 'message',
  GENERAL: 'general'
};

/**
 * Notification priority levels
 */
export const NOTIFICATION_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Create a notification for specific users
 * @param {string} familyCode - Family code
 * @param {Object} notificationData - Notification data
 * @returns {Promise<void>}
 */
export const createNotification = async (familyCode, notificationData) => {
  try {
    const {
      recipientIds = [], // Array of user IDs to notify
      type,
      title,
      message,
      priority = NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl = null,
      actionLabel = null,
      metadata = {},
      createdBy,
      createdByName
    } = notificationData;

    if (!familyCode || !type || !title || !message) {
      throw new Error('Missing required notification fields');
    }

    if (!recipientIds || recipientIds.length === 0) {
      throw new Error('At least one recipient is required');
    }

    // Create notification for each recipient
    const notificationsRef = collection(db, 'families', familyCode, 'notifications');
    const batch = writeBatch(db);

    for (const recipientId of recipientIds) {
      const notificationRef = doc(notificationsRef);
      batch.set(notificationRef, {
        recipientId,
        type,
        title,
        message,
        priority,
        actionUrl,
        actionLabel,
        metadata,
        createdBy,
        createdByName,
        isRead: false,
        deleted: false,
        createdAt: serverTimestamp(),
        readAt: null
      });
    }

    await batch.commit();
  } catch (error) {
    logger.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Create a notification for all family members except the creator
 * @param {string} familyCode - Family code
 * @param {Object} notificationData - Notification data
 * @param {string} excludeUserId - User ID to exclude (usually the creator)
 * @returns {Promise<void>}
 */
export const createNotificationForFamily = async (familyCode, notificationData, excludeUserId = null) => {
  try {
    // Get all family members
    const membersRef = collection(db, 'families', familyCode, 'members');
    const membersSnapshot = await getDocs(membersRef);

    const recipientIds = [];
    membersSnapshot.forEach(doc => {
      if (!excludeUserId || doc.id !== excludeUserId) {
        recipientIds.push(doc.id);
      }
    });

    if (recipientIds.length === 0) {
      return;
    }

    await createNotification(familyCode, {
      ...notificationData,
      recipientIds
    });
  } catch (error) {
    logger.error('Error creating family notification:', error);
    throw error;
  }
};

/**
 * Get notifications for a specific user
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @param {number} maxResults - Maximum number of notifications to fetch
 * @returns {Promise<Array>} Array of notifications
 */
export const getNotifications = async (familyCode, userId, maxResults = 50) => {
  try {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const notificationsRef = collection(db, 'families', familyCode, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('recipientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const snapshot = await getDocs(notificationsQuery);
    const notifications = [];

    snapshot.forEach(doc => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return notifications;
  } catch (error) {
    logger.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count for a user
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @returns {Promise<number>} Unread count
 */
export const getUnreadCount = async (familyCode, userId) => {
  try {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const notificationsRef = collection(db, 'families', familyCode, 'notifications');
    const unreadQuery = query(
      notificationsRef,
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);
    return snapshot.size;
  } catch (error) {
    logger.error('Error fetching unread count:', error);
    return 0;
  }
};

/**
 * Mark a notification as read
 * @param {string} familyCode - Family code
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markAsRead = async (familyCode, notificationId) => {
  try {
    if (!familyCode || !notificationId) {
      throw new Error('Family code and notification ID are required');
    }

    const notificationRef = doc(db, 'families', familyCode, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markAllAsRead = async (familyCode, userId) => {
  try {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const notificationsRef = collection(db, 'families', familyCode, 'notifications');
    const unreadQuery = query(
      notificationsRef,
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);

    if (snapshot.empty) {
      return;
    }

    const batch = writeBatch(db);
    snapshot.forEach(doc => {
      batch.update(doc.ref, {
        isRead: true,
        readAt: serverTimestamp()
      });
    });

    await batch.commit();
  } catch (error) {
    logger.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} familyCode - Family code
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (familyCode, notificationId) => {
  try {
    if (!familyCode || !notificationId) {
      throw new Error('Family code and notification ID are required');
    }

    const notificationRef = doc(db, 'families', familyCode, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time notifications for a user
 * @param {string} familyCode - Family code
 * @param {string} userId - User ID
 * @param {Function} onUpdate - Callback function when notifications update
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (familyCode, userId, onUpdate) => {
  try {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const notificationsRef = collection(db, 'families', familyCode, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('recipientId', '==', userId),
      where('deleted', '!=', true),
      orderBy('deleted'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifications = [];
        snapshot.forEach(doc => {
          notifications.push({
            id: doc.id,
            ...doc.data()
          });
        });
        onUpdate(notifications);
      },
      (error) => {
        logger.error('Error in notification subscription:', error);
        // Call onUpdate with empty array so callers can clear their loading state
        onUpdate([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    logger.error('Error subscribing to notifications:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Helper: Create announcement notification
 */
export const notifyNewAnnouncement = async (familyCode, announcement, createdBy, createdByName) => {
  await createNotificationForFamily(
    familyCode,
    {
      type: NOTIFICATION_TYPES.ANNOUNCEMENT,
      title: 'New Announcement',
      message: `${createdByName} posted: ${announcement.title}`,
      priority: announcement.urgent ? NOTIFICATION_PRIORITY.HIGH : NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl: '/announcements',
      actionLabel: 'View Announcement',
      metadata: { announcementId: announcement.id },
      createdBy,
      createdByName
    },
    createdBy
  );
};

/**
 * Helper: Create event notification
 */
export const notifyNewEvent = async (familyCode, event, createdBy, createdByName) => {
  await createNotificationForFamily(
    familyCode,
    {
      type: NOTIFICATION_TYPES.EVENT,
      title: 'New Event',
      message: `${createdByName} created event: ${event.title}`,
      priority: NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl: '/events',
      actionLabel: 'View Event',
      metadata: { eventId: event.id },
      createdBy,
      createdByName
    },
    createdBy
  );
};

/**
 * Helper: Create health emergency notification
 */
export const notifyHealthEmergency = async (familyCode, healthUpdate, createdBy, createdByName) => {
  await createNotificationForFamily(
    familyCode,
    {
      type: NOTIFICATION_TYPES.HEALTH_EMERGENCY,
      title: '🚨 Health Emergency',
      message: `${createdByName}: ${healthUpdate.title || 'Medical emergency reported'}`,
      priority: NOTIFICATION_PRIORITY.URGENT,
      actionUrl: '/health',
      actionLabel: 'View Details',
      metadata: { healthUpdateId: healthUpdate.id },
      createdBy,
      createdByName
    },
    createdBy
  );
};

/**
 * Helper: Create member joined notification
 */
export const notifyMemberJoined = async (familyCode, newMemberName, newMemberId) => {
  await createNotificationForFamily(
    familyCode,
    {
      type: NOTIFICATION_TYPES.MEMBER_JOINED,
      title: 'New Family Member',
      message: `${newMemberName} has joined the family!`,
      priority: NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl: '/family-board',
      actionLabel: 'Welcome Them',
      metadata: { newMemberId },
      createdBy: newMemberId,
      createdByName: newMemberName
    },
    newMemberId
  );
};

/**
 * Helper: Create tag notifications for tagged members
 * @param {string} familyCode - Family code
 * @param {Array} taggedMemberIds - Array of user IDs who were tagged
 * @param {Object} contentDetails - Details about the content
 *   - type: Content type (event, discussion, health, etc.)
 *   - id: Content ID
 *   - title: Content title
 *   - createdBy: User ID of creator
 *   - createdByName: Display name of creator
 *   - actionUrl: URL to view the content
 */
export const notifyTaggedMembers = async (familyCode, taggedMemberIds, contentDetails) => {
  if (!taggedMemberIds || taggedMemberIds.length === 0) {
    return;
  }

  const { type, id, title, createdBy, createdByName, actionUrl } = contentDetails;

  // Filter out the creator from tagged members (don't notify yourself)
  const recipientIds = taggedMemberIds.filter(memberId => memberId !== createdBy);

  if (recipientIds.length === 0) {
    return;
  }

  await createNotification(familyCode, {
    recipientIds,
    type: 'tag',
    title: 'You were tagged',
    message: `${createdByName} tagged you in a ${type}: ${title}`,
    priority: NOTIFICATION_PRIORITY.MEDIUM,
    actionUrl: actionUrl || `/${type}s`,
    actionLabel: 'View',
    metadata: { contentType: type, contentId: id },
    createdBy,
    createdByName
  });
};

const notificationService = {
  createNotification,
  createNotificationForFamily,
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  subscribeToNotifications,
  notifyNewAnnouncement,
  notifyNewEvent,
  notifyHealthEmergency,
  notifyMemberJoined,
  notifyTaggedMembers,
  NOTIFICATION_TYPES,
  NOTIFICATION_PRIORITY
};

export default notificationService;
